import React, { useState } from 'react';
import { X, Wallet, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useVertical } from '../../context/VerticalContext';
import { errorMessage } from '../../lib/errors';
import { localDate } from '../../lib/periods';

interface RecordDrawingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const RecordDrawingModal: React.FC<RecordDrawingModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { activeOrgId } = useVertical();
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
            setError('Please enter a valid amount.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // 1. Insert Header
            const { data: header, error: headerError } = await supabase
                .from('ledger_headers')
                .insert({
                    org_id: activeOrgId,
                    user_id: user.id,
                    // Local day, not UTC: just after midnight in BST, toISOString()
                    // dated the drawing yesterday (last month, on the 1st).
                    date: localDate(new Date()),
                    vendor: 'Owner Drawings',
                    total_gross: Number(amount),
                    vat_amount: 0,
                    transaction_type: 'drawing',
                    status: 'posted'
                })
                .select()
                .single();

            if (headerError) throw headerError;

            // 2. Insert Line Item
            const { error: lineError } = await supabase
                .from('ledger_line_items')
                .insert({
                    org_id: activeOrgId,
                    header_date: header.date,
                    header_id: header.id,
                    description: 'Owner Drawing recorded',
                    quantity: 1,
                    unit_price: Number(amount),
                    total: Number(amount),
                    category: 'Drawings'
                });

            if (lineError) throw lineError;

            setIsSuccess(true);
            setTimeout(() => {
                onSuccess();
                setIsSuccess(false);
                setAmount('');
            }, 1500);

        } catch (err) {
            console.error('Error inserting drawing:', err);
            setError(errorMessage(err) || 'Failed to record drawing.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-scrim/70
                px-4 pt-[max(1.5rem,env(safe-area-inset-top))]
                pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-6"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-panel border border-line rounded-2xl shadow-card w-full max-w-md max-h-full overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-line">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 grid place-items-center rounded-xl bg-gold-fill/10 border border-gold/20 text-gold">
                            <Wallet className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-display font-semibold text-fg tracking-tight">Pay Yourself</h2>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="p-2 -mr-2 text-fg-3 hover:text-fg hover:bg-card rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 sm:px-6 py-5">
                    {isSuccess ? (
                        <div className="py-8 flex flex-col items-center justify-center space-y-4">
                            <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center text-success">
                                <CheckCircle className="w-8 h-8" />
                            </div>
                            <p className="text-success font-medium">Drawing recorded successfully.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.16em] font-display font-semibold text-fg-3 mb-2">
                                    Amount to Withdraw (£)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-4 font-mono">£</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="w-full h-12 bg-canvas border border-line rounded-lg py-2.5 pl-8 pr-4 text-lg text-fg font-mono tabular-nums outline-none focus:border-gold focus:shadow-focus transition-all"
                                        placeholder="0.00"
                                        autoFocus
                                        required
                                    />
                                </div>
                                <p className="text-xs text-fg-3 mt-2 leading-relaxed">
                                    This reduces your Safe to Spend but does <strong className="text-fg-2">not</strong> affect your Tax, VAT, or Net Profit calculations.
                                </p>
                            </div>

                            {error && (
                                <div className="p-3 rounded-lg bg-danger/10 border border-danger/30 text-danger text-sm">
                                    {error}
                                </div>
                            )}

                            <div className="flex gap-3 justify-end pt-1">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="h-11 px-4 text-sm font-display font-semibold text-fg-3 hover:text-fg bg-card hover:bg-raised border border-line rounded-lg transition-colors disabled:opacity-50"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="fd-btn fd-btn-brass h-11 px-5"
                                    disabled={loading}
                                >
                                    {loading ? 'Processing…' : 'Record Drawing'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
